import { useState } from 'react';

type EditorRow<T> = { id: string; value: T };

function createRows<T>(items: T[]): EditorRow<T>[] {
  return items.map((value) => ({ id: crypto.randomUUID(), value }));
}

/** Keep UI identity separate from editable field keys and option values. */
export function useOutcomeEditorRows<T>(
  items: T[],
  onChange: (items: T[]) => void,
) {
  const [state, setState] = useState(() => ({
    items,
    rows: createRows(items),
  }));
  let rows = state.rows;
  if (items !== state.items) {
    // An external reset/load replaces this editor's draft. Ordinary edits below
    // save the exact array passed to onChange, preserving each row's identity.
    rows = createRows(items);
    setState({ items, rows });
  }

  function changeRows(nextRows: EditorRow<T>[]) {
    const nextItems = nextRows.map((row) => row.value);
    setState({ items: nextItems, rows: nextRows });
    onChange(nextItems);
  }

  return {
    rows,
    update: (id: string, value: T) =>
      changeRows(rows.map((row) => (row.id === id ? { ...row, value } : row))),
    remove: (id: string) => changeRows(rows.filter((row) => row.id !== id)),
    /** Appends a row and returns its UI id. */
    add: (value: T) => {
      const id = crypto.randomUUID();
      changeRows([...rows, { id, value }]);
      return id;
    },
  };
}
